import React, { useEffect, useState } from "react";
import { api, fmt } from "@/lib/api";

const SIGNAL_COLOR = {
  pass: { bg: "#e8f3ec", border: "#a3d4b2", color: "#1e5d34" },
  attention: { bg: "#fef3e6", border: "#f1c89a", color: "#8a5a14" },
};

export default function Readiness() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.readinessState()
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message || "Failed to load"); setLoading(false); });
  }, []);

  if (loading) {
    return <div className="loading" data-testid="readiness-loading">Loading readiness state…</div>;
  }
  if (error) {
    return <div className="loading">{error}</div>;
  }

  const { signals, summary, periods } = data;
  const isGo = summary.decision === "go";

  return (
    <div data-testid="readiness-page">
      <header style={{ marginBottom: 18 }}>
        <div className="heading h1" data-testid="readiness-title">Parallel Cycle Readiness</div>
        <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
          Go/no-go decision support for Parallel Close Cycle #1 in staging. Operational dashboard — not an archival artifact.
        </div>
      </header>

      {/* Top: go/no-go banner */}
      <div data-testid="readiness-decision-banner" style={{
        background: isGo ? "#e8f3ec" : "#fef3e6",
        border: `1px solid ${isGo ? "#a3d4b2" : "#f1c89a"}`,
        color: isGo ? "#1e5d34" : "#8a5a14",
        padding: "16px 20px", borderRadius: 8, marginBottom: 24,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          fontSize: 22, fontWeight: 700, letterSpacing: "0.05em",
        }} data-testid="readiness-decision">
          {isGo ? "GO" : "NO-GO"}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>
          {isGo
            ? "All readiness signals are PASS. Parallel Cycle #1 may proceed."
            : `${summary.attention_count} signal(s) require attention before Parallel Cycle #1 can proceed.`}
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            Generated {fmt.dateTime(data.generated_at)}
          </div>
        </div>
      </div>

      {/* Signals grid */}
      <div data-testid="readiness-signals" style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: 14, marginBottom: 28,
      }}>
        {signals.map(sig => {
          const tone = SIGNAL_COLOR[sig.status] || SIGNAL_COLOR.attention;
          return (
            <div key={sig.id} data-testid={`signal-${sig.id}`} style={{
              background: "var(--surface)", border: `1px solid var(--line)`,
              borderRadius: 8, padding: 16,
              borderTop: `4px solid ${tone.border}`,
            }}>
              <div style={{ fontSize: 12, color: "var(--ink-mute)", marginBottom: 8 }}>
                {sig.label}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                <div style={{ fontSize: 30, fontWeight: 700 }}>{sig.value}</div>
                <span style={{
                  background: tone.bg, color: tone.color, padding: "2px 8px",
                  borderRadius: 12, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.04em", textTransform: "uppercase",
                }} data-testid={`signal-${sig.id}-status`}>
                  {sig.status === "pass" ? "PASS" : "ATTENTION"}
                </span>
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}>
                {sig.hint}
              </div>
            </div>
          );
        })}
      </div>

      {/* Per-period readiness */}
      <div data-testid="readiness-periods" style={{
        background: "var(--surface)", border: "1px solid var(--line)",
        borderRadius: 8, padding: 20,
      }}>
        <div className="heading h2" style={{ fontSize: 16, marginBottom: 10 }}>
          Per-period readiness
        </div>
        <table className="table" style={{ width: "100%", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Account</th>
              <th style={{ textAlign: "left" }}>Period</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Under review</th>
              <th style={{ textAlign: "right" }}>Open recovery recs</th>
              <th>Snapshot</th>
              <th>Last binder</th>
            </tr>
          </thead>
          <tbody>
            {periods.length === 0 ? (
              <tr><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 14 }}>
                No periods found.
              </td></tr>
            ) : periods.map(p => (
              <tr key={p.period_id} data-testid={`period-row-${p.period_id}`}>
                <td>{p.account_name}</td>
                <td>{p.period_label}</td>
                <td style={{ textAlign: "center" }}>
                  <span className="tag" style={{ fontSize: 11 }}>{p.status}</span>
                </td>
                <td style={{ textAlign: "right" }}>{p.under_review}</td>
                <td style={{ textAlign: "right",
                             color: p.open_recovery_recs > 0 ? "#8a5a14" : "inherit",
                             fontWeight: p.open_recovery_recs > 0 ? 600 : 400 }}>
                  {p.open_recovery_recs}
                </td>
                <td style={{ textAlign: "center" }}>
                  {p.has_snapshot ? "✓" : <span className="muted">—</span>}
                </td>
                <td>{p.last_binder_generated ? fmt.dateTime(p.last_binder_generated) : <span className="muted">never</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Go/no-go criteria reminder */}
      <div className="muted" style={{ fontSize: 12, marginTop: 18, padding: "12px 16px",
        background: "var(--secondary)", borderRadius: 6, lineHeight: 1.6 }}>
        <b>Decision criteria.</b> Parallel Cycle #1 may proceed when:&nbsp;
        M-6 = 0 (or ratified) · M-10 = 0 (or ratified) ·
        Open recovery recommendations dispositioned ·
        V-10 binder diff complete (manual Controller step) ·
        <b>and</b> every locked period is explicitly attested (no un-attested locks).
      </div>
    </div>
  );
}

function AttestedCell({ periodId }) {
  const [cert, setCert] = useState(undefined);
  const [drift, setDrift] = useState(null);
  useEffect(() => {
    api.latestCertification(periodId).then(setCert).catch(() => setCert(null));
    api.governanceDrift(periodId).then(setDrift).catch(() => setDrift(null));
  }, [periodId]);
  if (cert === undefined) return <span className="muted">…</span>;
  if (!cert) return <span className="muted" title="No certification">—</span>;
  if (drift?.drifted) {
    return <span title={`Drift: ${drift.severity}`}
      style={{ color: drift.severity === "CRITICAL" ? "#8a2a0c"
        : drift.severity === "WARNING" ? "#8a5a14" : "#1f4d8a", fontWeight: 700 }}>
      ⚠
    </span>;
  }
  return <span style={{ color: "#1e5d34", fontWeight: 700 }}>✓</span>;
}
